import React, { createContext, useContext, useState } from "react";
import { Badge, Button, ConfigProvider, Popover, Select } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import { useWidth } from "../../../hooks";

const ZINDEX_POPUP = 1300;
const ZINDEX_DROPDOWN = 1400;
const COLLAPSED_BREAKPOINTS = ["xs", "sm", "md"];

const ToolbarFiltersContext = createContext(false);

/** Select de antd con busqueda y boton para limpiar, listo para usar en el toolbar de las tablas */
interface FilterSelectProps {
  label: string;
  options: Array<string | { label: string; value: string | number }>;
  value?: string | number | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  width?: number;
}

export const FilterSelect = ({
  label,
  options,
  value,
  onChange,
  disabled,
  width = 220,
}: FilterSelectProps) => {
  const collapsed = useContext(ToolbarFiltersContext);

  return (
    <Select
      showSearch
      allowClear
      placeholder={label}
      disabled={disabled}
      optionFilterProp="label"
      style={collapsed ? { width: "100%" } : { width, minWidth: width }}
      dropdownStyle={{ zIndex: ZINDEX_DROPDOWN }}
      options={options.map((el) =>
        typeof el === "string" ? { label: el, value: el } : el
      )}
      value={value === "" ? null : value}
      onChange={(val) => onChange(val ?? null)}
    />
  );
};

interface ToolbarFiltersProps {
  /** Cantidad de filtros activos, se muestra en el boton cuando esta colapsado */
  activeCount?: number;
  children: React.ReactNode;
}

/**
 * En escritorio muestra los filtros en linea; en mobil y tablet los agrupa
 * dentro de un popup de antd detras de un boton "Filtros".
 */
export const ToolbarFilters = ({
  activeCount = 0,
  children,
}: ToolbarFiltersProps) => {
  const width = useWidth();
  const [open, setOpen] = useState(false);
  const collapsed = COLLAPSED_BREAKPOINTS.includes(width);

  return (
    <ConfigProvider
      theme={{
        components: {
          Popover: { zIndexPopup: ZINDEX_POPUP },
          Select: { zIndexPopup: ZINDEX_DROPDOWN },
          DatePicker: { zIndexPopup: ZINDEX_DROPDOWN },
        },
      }}
    >
      <ToolbarFiltersContext.Provider value={collapsed}>
        {collapsed ? (
          <Popover
            trigger="click"
            title="Filtros"
            placement="bottomLeft"
            open={open}
            onOpenChange={setOpen}
            forceRender
            content={
              <Box
                className="toolbar-filters-popup"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  width: { xs: "calc(100vw - 80px)", sm: 320 },
                  maxWidth: 320,
                  "& .ant-select, & .ant-picker": {
                    width: "100% !important",
                    minWidth: "0 !important",
                  },
                  "& .MuiGrid-root": { width: "100%", maxWidth: "100%" },
                }}
              >
                {children}
              </Box>
            }
          >
            <Badge count={activeCount} size="small" offset={[-4, 4]}>
              <Button icon={<FilterOutlined />}>Filtros</Button>
            </Badge>
          </Popover>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {children}
          </Box>
        )}
      </ToolbarFiltersContext.Provider>
    </ConfigProvider>
  );
};
